import {
  DeleteItemCommand,
  DynamoDBClient,
  GetItemCommand,
  PutItemCommand,
  QueryCommand,
  UpdateItemCommand,
} from '@aws-sdk/client-dynamodb';
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb';
import {
  DeleteItemInput,
  GetItemInput,
  MarshalledInput,
  PutItemInput,
  QueryItemsInput,
  UnmarshalledInput,
  UpdateItemInput,
} from './types';

function generateRandomString(length = 5) {
  const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    const randomChar = characters.charAt(randomIndex);
    result += randomChar;
  }

  return result;
}

export class DynamoClient {
  private ddbClient: DynamoDBClient;

  constructor({
    region,
    credentials,
  }: {
    region: string;
    credentials: {
      secretAccessKey: string;
      accessKeyId: string;
    };
  }) {
    this.ddbClient = new DynamoDBClient({
      region,
      credentials,
    });
  }

  generateSecondaryResourceId(resourceId: string) {
    return `${resourceId}-${generateRandomString()}`;
  }

  processCommandInput<T extends UnmarshalledInput>(
    unmarshalledInput: T,
  ): MarshalledInput<T> {
    const marshalledInput: any = { ...unmarshalledInput };

    if ('Key' in marshalledInput) {
      marshalledInput.Key = marshall(marshalledInput['Key'], {
        removeUndefinedValues: true,
      });
    }

    if ('Item' in marshalledInput) {
      marshalledInput.Item = marshall(marshalledInput['Item'], {
        removeUndefinedValues: true,
      });
    }

    if ('ExpressionAttributeValues' in marshalledInput) {
      marshalledInput.ExpressionAttributeValues = marshall(
        marshalledInput['ExpressionAttributeValues'],
        { removeUndefinedValues: true },
      );
    }

    if ('AttributeUpdates' in marshalledInput) {
      marshalledInput.AttributeUpdates = marshall(
        marshalledInput['AttributeUpdates'],
        { removeUndefinedValues: true },
      );
    }

    return marshalledInput;
  }

  async putItem(input: PutItemInput) {
    const command = new PutItemCommand(this.processCommandInput(input));
    await this.ddbClient.send(command);
    return input.Item;
  }

  async deleteItem(input: DeleteItemInput) {
    const command = new DeleteItemCommand(this.processCommandInput(input));
    const res = await this.ddbClient.send(command);
    if (!res.Attributes) return;
    return unmarshall(res.Attributes);
  }

  async getItem(input: GetItemInput) {
    const command = new GetItemCommand(this.processCommandInput(input));
    const res = await this.ddbClient.send(command);
    if (!res.Item) return;
    return unmarshall(res.Item);
  }

  async updateItem(input: UpdateItemInput) {
    const command = new UpdateItemCommand(this.processCommandInput(input));
    const res = await this.ddbClient.send(command);
    if (!res.Attributes) return;
    return unmarshall(res.Attributes);
  }

  async queryItems(input: QueryItemsInput) {
    let command = new QueryCommand(this.processCommandInput(input));
    let res = await this.ddbClient.send(command);

    if (!res.Items) return;

    const items = res.Items.map((item) => unmarshall(item));

    // DynamoDB limits the amount of data you can query on one request
    // Using LastEvaluatedKey will continue the query where the last left off
    while (res.LastEvaluatedKey) {
      command = new QueryCommand(
        this.processCommandInput({
          ...input,
          ExclusiveStartKey: res.LastEvaluatedKey,
        }),
      );

      res = await this.ddbClient.send(command);

      if (!res.Items) return items;
      res.Items.forEach((item) => items.push(unmarshall(item)));
    }

    return items;
  }
}
